import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import "./Chatbot.css";
import commonFunction from "./admin/common/commonFunction";

const ChatbotIcon = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [loading, setLoading] = useState(false);
  const chatBoxRef = useRef(null);
  const iconRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [previousChats, setPreviousChats] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [pages, setPages] = useState([]);
  const [showPages, setShowPages] = useState(false);

  // Auto-scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const userData = JSON.parse(localStorage.getItem("loginResponse"));
  const UserId = userData?.data?.data?.id || null;

  useEffect(() => {
    scrollToBottom();
  }, [messages, showPages]);

  // Close chatbot when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        chatBoxRef.current &&
        !chatBoxRef.current.contains(event.target) &&
        iconRef.current &&
        !iconRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    const storedLoginResponse = localStorage.getItem("loginResponse");
    const loginResponse = storedLoginResponse
      ? JSON.parse(storedLoginResponse)
      : null;
    const token = loginResponse?.data?.tokan || localStorage.getItem("adminToken");

    const response = commonFunction.fetchRecentChats(UserId, token);
    response.then((res) => {
      setPreviousChats(res.data);
    });
  }, [showSuggestions]);


  const sendMessage = async (message = null, conversationId = null) => {
    const messageToSend = message || input;
    if (!messageToSend.trim()) return;

    // Add user message
    const userMessage = { type: "user", text: messageToSend };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setShowSuggestions(false);
    setLoading(true);
    setPages([]);
    setShowPages(false);

    const storedLoginResponse = localStorage.getItem("loginResponse");
    const loginResponse = storedLoginResponse
      ? JSON.parse(storedLoginResponse)
      : null;
    const token = loginResponse?.data?.tokan || localStorage.getItem("adminToken");
    try {
      const params = new URLSearchParams();
      params.append("message", messageToSend); // use dynamic message
      params.append("user_id", UserId);
      const finalConversationId = conversationId || activeConversationId;
      if (finalConversationId) {
        params.append("conversation_id", finalConversationId);
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}chatbot/getResponse`,
        params,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const newConversationId = res.data?.data?.conversation_id;

      if (newConversationId && !activeConversationId) {
        setActiveConversationId(newConversationId);
      }

      const botMessage = { type: "bot", text: res?.data?.ai_response };
      setMessages((prev) => [...prev, botMessage]);
    } catch (err) {
      console.error(err);
      const errorMessage = {
        type: "bot",
        text: "Sorry, I couldn't process your request. Please try again.",
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPages = async () => {
    try {
      const storedLoginResponse = localStorage.getItem("loginResponse");
      const loginResponse = storedLoginResponse
        ? JSON.parse(storedLoginResponse)
        : null;

      const token =
        loginResponse?.data?.tokan || localStorage.getItem("adminToken");

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}pages`, // change API endpoint
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPages(res.data?.data || []);
      setShowPages(true);

      // Optional: show message in chat
      setMessages((prev) => [
        ...prev,
        { type: "bot", text: "Here are all pages:" },
      ]);
    } catch (error) {
      console.error(error);
      setShowPages(false);
    }
  };

  const handleEditPage = (page) => {
    localStorage.setItem('ID', page?.id);
    localStorage.setItem('Banner URL', page?.bannerurl);
    localStorage.setItem('Banner Title', page?.bannertitle);
    localStorage.setItem('Banner Description', page?.bannerdesc);
    localStorage.setItem('Page Title', page?.pagetitle);
    localStorage.setItem('Page Description', page?.pagedesc);
    localStorage.setItem('Page Template', page?.template);
    localStorage.setItem('Page Slug', page?.slug);
    localStorage.setItem('Page Sort', page?.sort);
    localStorage.setItem('Top menu', page?.top_menu);
    localStorage.setItem('Page Image', page?.pageImage);

    window.location.href = `/admin/updatepagedata`;
  };

  const handleCreateProfile = () => {
    window.location.href = `/admin/lady-profile`;
  };

  // Handle suggestion click
  const handleSuggestionClick = (question) => {
    const conversationId = question?.id;
    setActiveConversationId(conversationId);
    sendMessage(question?.title, conversationId);
  };

  // Handle Enter key to send message
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Clear chat history
  const clearChat = () => {
    setMessages([]);
    setShowSuggestions(true);
    setShowPages(false);
  };

  const sanitize = (html) => {
    return html.replace(/<script.*?>.*?<\/script>/gi, "");
  };

  return (
    <>
      {/* Overlay backdrop */}
      {open && <div className="chatbot-overlay" onClick={() => setOpen(false)}></div>}

      {/* Icon */}
      <div
        className="chatbot-icon"
        onClick={() => setOpen(!open)}
        ref={iconRef}
        title="Chat with AI Assistant"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      </div>

      {/* Chat Window */}
      {open && (
        <div className="chatbot-box" ref={chatBoxRef}>
          <div className="chatbotscroll">
            <div className="chat-header">
              <div className="chat-title-wrapper">
                <div className="chat-title-main">
                  <div className="chat-icon-wrapper">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12c0 1.54.36 3 .97 4.29L2 22l6.29-.97C9.29 21.61 10.5 22 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2m0 18c-1.41 0-2.73-.27-3.97-.77l-.28-.14-2.91.44.44-2.91-.14-.28C4.27 14.73 4 13.41 4 12c0-4.41 3.59-8 8-8s8 3.59 8 8-3.59 8-8 8m3.64-12.67c-.15-.15-.4-.19-.61-.04l-2.46 1.55c-.16.1-.26.28-.23.46.07.41.11.85.05 1.3-.15 1.1-.82 2.06-1.81 2.52-.35.16-.61.45-.64.8-.03.35.16.68.49.83 1.12.52 2.36.52 3.48 0 1.48-.68 2.52-2.11 2.73-3.72.11-.81-.07-1.57-.3-2.23-.11-.31-.37-.52-.7-.52-.08 0-.17.01-.25.04l-1.35.58z"></path>
                    </svg>
                  </div>
                  <div className="chat-title-text">
                    <h3 className="chat-title">Admin Assistant</h3>
                    <p className="chat-subtitle">AI-Powered Support</p>
                  </div>
                </div>
                <div className="chat-status-badge">
                  <span className="status-dot"></span>
                  <span className="status-text">Online</span>
                </div>
              </div>
              <div className="chat-header-actions">
                <button
                  className="chat-action-btn"
                  onClick={clearChat}
                  title="Clear chat"
                  aria-label="Clear chat history"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36M20.49 15a9 9 0 0 1-14.85 3.36"></path>
                  </svg>
                </button>
                <button
                  className="chat-close-btn"
                  onClick={() => setOpen(false)}
                  title="Close chat"
                  aria-label="Close chat window"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>

            <div className="chat-messages">
              {messages.length === 0 && showSuggestions && (
                <div className="welcome-message">
                  <p>👋 Welcome, Admin!</p>
                  <p className="welcome-subtitle">
                    Click on any question below or type your own
                  </p>
                </div>
              )}

              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={msg.type === "user" ? "user-msg" : "bot-msg"}
                >
                  <div className="msg-content" style={{ whiteSpace: "pre-line" }}>
                    <div
                      dangerouslySetInnerHTML={{ __html: sanitize(msg.text) }}
                    />
                  </div>
                </div>
              ))}

              {/* ✅ PAGE LIST */}
              {showPages && (
                <div className="bot-msg">
                  <div className="msg-content">
                    <div className="pages-card">
                      <div className="pages-header">
                        <h4>📄 Pages</h4>
                        <span>{pages.length} Total</span>
                      </div>

                      <div className="pages-list">
                        {pages.map((page) => (
                          <div key={page.id} className="page-item">
                            <div className="page-info">
                              <span className="page-title">{page.pagetitle}</span>
                            </div>

                            <div className="page-actions">
                              <button
                                className="edit-btn"
                                onClick={() => handleEditPage(page)}
                              >
                                ✏️ Edit
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {loading && (
                <div className="bot-msg">
                  <div className="msg-content typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Suggestions */}
            {showSuggestions && messages.length === 0 && (
              <div className="chat-suggestions">
                <div className="suggestions-title">Quick Questions:</div>
                <div className="suggestions-grid">
                  {/* NEW BUTTON */}
                  <button
                    className="suggestion-btn"
                    onClick={fetchPages}
                  >
                    Edit Pages
                  </button>
                  <button
                    className="suggestion-btn"
                    onClick={handleCreateProfile}
                  >
                    Create a woman's profile
                  </button>
                  {previousChats?.map((question, index) => (
                    <button
                      key={index}
                      className="suggestion-btn"
                      onClick={() => handleSuggestionClick(question)}
                    >
                      {question?.title}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="chat-input">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your question..."
              autoFocus
              disabled={loading}
            />
            <button
              onClick={() => sendMessage()}
              className="send-btn"
              title="Send message"
              disabled={loading}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatbotIcon;